import { useEffect, useRef } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { AuthProvider } from '@/contexts/AuthProvider';
import { ThemeProvider } from '@/contexts/ThemeProvider';
import { SidebarProvider } from '@/contexts/SidebarProvider';
import { useTelegramKeyboardScrollFix } from '@/hooks/useTelegramKeyboardScrollFix';
import { useTelegram } from '@/hooks/useTelegram';
import { buildJoinPath, parseInviteStartParam } from '@/utils/inviteLink';
import { apiClient } from '@/services/api';
import { USER_ROLES } from '@/constants';
import 'react-toastify/dist/ReactToastify.css';
import HomePage from '@/pages/HomePage';
import LandingPage from '@/pages/LandingPage';
import LoginPage from '@/pages/LoginPage';
import SignUpPage from '@/pages/SignUpPage';
import TournamentsListLayout from '@/layouts/TournamentsListLayout';
import MyTournamentsTab from '@/pages/tournaments-list/MyTournamentsTab';
import PublicTournamentsTab from '@/pages/tournaments-list/PublicTournamentsTab';
import ArchivedTournamentsTab from '@/pages/tournaments-list/ArchivedTournamentsTab';
import TournamentDetailLayout from '@/layouts/TournamentDetailLayout';
import TournamentMatchesTab from '@/pages/tournament-detail/MatchesTab';
import TournamentParticipantsTab from '@/pages/tournament-detail/ParticipantsTab';
import TournamentLeaderboardTab from '@/pages/tournament-detail/LeaderboardTab';
import TournamentSettingsTab from '@/pages/tournament-detail/SettingsTab';
import MatchDetailLayout from '@/layouts/MatchDetailLayout';
import MatchDetailsTab from '@/pages/match-detail/DetailsTab';
import MatchPredictionsTab from '@/pages/match-detail/PredictionsTab';
import LeaguesListLayout from '@/layouts/LeaguesListLayout';
import AllLeaguesTab from '@/pages/leagues-list/AllLeaguesTab';
import MatchesByDateTab from '@/pages/leagues-list/MatchesByDateTab';
// BP-84 EXPERIMENT — api-football widgets evaluation page.
import TestWidgetsPage from '@/pages/TestWidgetsPage';
import LeagueDetailLayout from '@/layouts/LeagueDetailLayout';
import LeagueMatchesTab from '@/pages/league-detail/MatchesTab';
import LeaguePlayoffTab from '@/pages/league-detail/PlayoffTab';
import LeagueStandingsTab from '@/pages/league-detail/StandingsTab';
import LeagueTournamentsTab from '@/pages/league-detail/TournamentsTab';
import MatchOverviewLayout from '@/layouts/MatchOverviewLayout';
import MatchOverviewTab from '@/pages/match-overview/OverviewTab';
import MatchTournamentsTab from '@/pages/match-overview/TournamentsTab';
import ParticipantStatsLayout from '@/layouts/ParticipantStatsLayout';
import ParticipantStatsStatsTab from '@/pages/participant-stats/StatsTab';
import AccountLayout from '@/layouts/AccountLayout';
import AdminLayout from '@/layouts/AdminLayout';
import AdminUsersTab from '@/pages/admin/UsersTab';
import AdminSettingsTab from '@/pages/admin/SettingsTab';
import AdminLeaguesTab from '@/pages/admin/LeaguesTab';
import AdminBookmakersTab from '@/pages/admin/BookmakersTab';
import AdminLeagueBookmakersTab from '@/pages/admin/LeagueBookmakersTab';
import AdminMatchesTab from '@/pages/admin/MatchesTab';
import AdminDebugApiTab from '@/pages/admin/DebugApiTab';
import EditTab from '@/pages/account/EditTab';
import MessagesTab from '@/pages/account/MessagesTab';
import SecurityTab from '@/pages/account/SecurityTab';
import InvitationAcceptPage from '@/pages/InvitationAcceptPage';
import JoinByLinkPage from '@/pages/JoinByLinkPage';
import VerifyEmailPage from '@/pages/VerifyEmailPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import AccountBlockedPage from '@/pages/AccountBlockedPage';
import TelegramWelcomePage from '@/pages/TelegramWelcomePage';
import DebugModeIndicator from '@/components/DebugModeIndicator/index';
import TournamentCreatePage from '@/pages/TournamentCreatePage';
import NotFoundPage from '@/pages/NotFoundPage';
import { ProtectedRoute, AdminRoute, ValidatedRoute, AccountRoute } from '@/components/RouteGuards';
import { AppLayout } from '@/components/layout';
import { useAuth } from '@/hooks/useAuth';
import './styles/main.scss';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 60_000,
      gcTime: 5 * 60_000,
    },
  },
});

// Redirects new TG users to /telegram-welcome
function TelegramWelcomeRedirect() {
  const { pendingTelegramWelcome, telegramUserData } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (pendingTelegramWelcome && telegramUserData) {
      navigate('/telegram-welcome', { replace: true });
    }
  }, [pendingTelegramWelcome, telegramUserData, navigate]);

  return null;
}

// Session key remembering the handled Mini App launch (start_param + initData auth_date), so a
// WebView reload of the same launch doesn't bounce the user back to /join after they moved on,
// while a deliberate relaunch with the same link (new auth_date) redirects again.
const HANDLED_START_PARAM_KEY = 'handledTelegramStartParam';

// Mini App opened via `t.me/<bot>/<app>?startapp=inv_<token>` → invite link landing (once per launch).
function TelegramStartParamRedirect() {
  const { webApp, startParam, isLoaded } = useTelegram();
  const navigate = useNavigate();
  const handledRef = useRef(false);
  const authDate = webApp?.initDataUnsafe?.auth_date ?? null;

  useEffect(() => {
    if (!isLoaded || handledRef.current) return;
    handledRef.current = true;

    const token = parseInviteStartParam(startParam);
    if (!token || !startParam) return;

    const launchKey = `${startParam}:${authDate ?? ''}`;
    try {
      if (sessionStorage.getItem(HANDLED_START_PARAM_KEY) === launchKey) return;
      sessionStorage.setItem(HANDLED_START_PARAM_KEY, launchKey);
    } catch {
      // sessionStorage unavailable — the ref still limits the redirect to this app boot.
    }

    navigate(buildJoinPath(token), { replace: true });
  }, [isLoaded, startParam, authDate, navigate]);

  return null;
}

function AppContent() {
  // Telegram mobile only: keep the focused field above the keyboard by scrolling (chrome never hides)
  useTelegramKeyboardScrollFix();

  // Conditionally load debug console based on admin setting
  useEffect(() => {
    let cleanup: (() => void) | undefined;

    // Skip if not authenticated — avoids 401 → interceptor redirect → infinite reload loop
    const token = localStorage.getItem('accessToken');
    if (!token) return;

    // In production, only admins can use debug console
    if (!import.meta.env.DEV) {
      try {
        const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
        if (currentUser.role !== USER_ROLES.ADMIN) return;
      } catch { return; }
    }

    // Check API setting (both dev and prod respect the checkbox)
    apiClient.getDebugMode().then(response => {
      const settings = response.data?.data;
      if (settings?.debug_console_enabled) {
        import('@/utils/debugConsole').then(({ initDebugConsole, destroyDebugConsole }) => {
          initDebugConsole();
          cleanup = destroyDebugConsole;
        });
      }
    }).catch(() => {
      // Silently fail — debug console is non-critical
    });

    return () => { cleanup?.(); };
  }, []);

  return (
    <Router>
      <SidebarProvider>
        <TelegramWelcomeRedirect />
        <TelegramStartParamRedirect />
        <DebugModeIndicator />
        <Routes>
          {/* Pages outside AppLayout — own shell or no shell */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/sign-up" element={<SignUpPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/telegram-welcome" element={<TelegramWelcomePage />} />

          {/* AppLayout — standard app shell (Header + SidebarNav + BottomNav) */}
          <Route element={<AppLayout />}>
            {/* Headless pages inside AppLayout (pages set showHeader=false via usePageConfig) */}
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/account-blocked" element={<AccountBlockedPage />} />
            <Route path="/invite/:inviteCode" element={<InvitationAcceptPage />} />
            {/* Invite link / QR landing — public, works for anonymous visitors */}
            <Route path="/join/:token" element={<JoinByLinkPage />} />

            {/* Protected routes — auth required */}
            <Route element={<ProtectedRoute />}>
              <Route path="/dashboard" element={<HomePage />} />
              {/* BP-84 EXPERIMENT — api-football widgets evaluation; not linked from nav. */}
              <Route path="/test-widgets-page" element={<TestWidgetsPage />} />
              <Route path="/tournaments" element={<TournamentsListLayout />}>
                <Route index element={<MyTournamentsTab />} />
                <Route path="public" element={<PublicTournamentsTab />} />
                <Route path="archived" element={<ArchivedTournamentsTab />} />
              </Route>
              <Route path="/tournaments/create" element={<TournamentCreatePage />} />
              {/* League browser. The detail children are every tab a league
                  can have; LeagueDetailLayout redirects the ones it lacks. */}
              <Route path="/matches" element={<LeaguesListLayout />}>
                <Route index element={<MatchesByDateTab />} />
                <Route path="leagues" element={<AllLeaguesTab />} />
              </Route>
              <Route path="/matches/:leagueId" element={<LeagueDetailLayout />}>
                <Route index element={<LeagueMatchesTab />} />
                <Route path="standings" element={<LeagueStandingsTab />} />
                <Route path="playoff" element={<LeaguePlayoffTab />} />
                <Route path="tournaments" element={<LeagueTournamentsTab />} />
              </Route>
              {/* Standalone match page. Children mirror MatchOverviewLayout's
                  `tabs` array, order included. */}
              <Route path="/match/:matchId" element={<MatchOverviewLayout />}>
                <Route index element={<MatchOverviewTab />} />
                <Route path="tournaments" element={<MatchTournamentsTab />} />
              </Route>
              <Route element={<AccountRoute />}>
                <Route path="/account" element={<AccountLayout />}>
                  <Route path="edit" element={<EditTab />} />
                  <Route path="messages" element={<MessagesTab />} />
                  <Route path="security" element={<SecurityTab />} />
                </Route>
              </Route>
              <Route element={<ValidatedRoute />}>
                <Route path="/tournaments/:id" element={<TournamentDetailLayout />}>
                  <Route index element={<TournamentMatchesTab />} />
                  <Route path="participants" element={<TournamentParticipantsTab />} />
                  <Route path="leaderboard" element={<TournamentLeaderboardTab />} />
                  <Route path="settings" element={<TournamentSettingsTab />} />
                </Route>
                {/* Per-participant statistics — standalone layout with its own
                    tab nav. Lives outside TournamentDetailLayout so the top
                    tab swiper shows participant-scoped tabs. v1 ships a
                    single "Статистика" tab (KPI + distribution + predictions
                    list merged); the route shape keeps the children-array
                    structure so additional tabs can be added later. */}
                <Route path="/tournaments/:tournamentId/leaderboard/:userId" element={<ParticipantStatsLayout />}>
                  <Route index element={<ParticipantStatsStatsTab />} />
                </Route>
                <Route path="/tournaments/:tournamentId/matches/:matchId" element={<MatchDetailLayout />}>
                  <Route index element={<MatchDetailsTab />} />
                  <Route path="predictions" element={<MatchPredictionsTab />} />
                </Route>
              </Route>
            </Route>

            {/* Admin routes — auth + admin role required */}
            <Route element={<AdminRoute />}>
              <Route path="/admin" element={<AdminLayout />}>
                <Route path="users" element={<AdminUsersTab />} />
                <Route path="bp-settings" element={<AdminSettingsTab />} />
                <Route path="leagues" element={<AdminLeaguesTab />} />
                <Route path="bookmakers" element={<AdminBookmakersTab />} />
                <Route path="league-bookmakers" element={<AdminLeagueBookmakersTab />} />
                <Route path="matches" element={<AdminMatchesTab />} />
                <Route path="debug-api" element={<AdminDebugApiTab />} />
              </Route>
            </Route>

            {/* Catch-all 404 */}
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
      </SidebarProvider>
    </Router>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <AppContent />
          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="colored"
          />
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
